'use client';

import React, { useState } from 'react';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { Character } from '../types';
import { useCurrentUser } from '@/contexts/authentication/client';
import { useCharacterMutations } from '@/contexts/characters/hooks';
import { mediaSrc } from '@/contexts/media/utils/mediaSrc';
import { Card } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/shared/components/ui/alert-dialog';
import {
  MoreVertical,
  Trash2,
  Pencil,
  User,
} from 'lucide-react';
import { isSystemCharacter } from '@/contexts/characters/utils/systemCharacter';

interface CharacterCardProps {
  character: Character;
  onEdit?: (character: Character) => void;
  onDelete?: (characterId: string) => void;
  /** Hide the edit/delete menu — used for book-scoped ("from stories") chars. */
  readOnly?: boolean;
}

export function CharacterCard({ character, onEdit, onDelete, readOnly = false }: CharacterCardProps) {
  const t = useTranslations('stories.characters');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { user } = useCurrentUser();
  const familyId = user?.familyId ?? undefined;
  const { deleteCharacter } = useCharacterMutations(familyId);

  const handleDelete = async () => {
    setDeleteError(null);

    try {
      const result = await deleteCharacter.mutateAsync(character.id);

      if (!result.success) {
        if (result.error.includes('used in')) {
          setDeleteError(
            t('deleteError.inUse', { count: character.usageCount })
          );
          return;
        }
        throw new Error(result.error);
      }

      onDelete?.(character.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Error deleting character:', error);
      setDeleteError(t('deleteError.generic'));
    }
  };

  const hasPortrait = !!character.primaryPortraitUrl;

  return (
    <>
      <Card
        data-testid={`character-card-${character.id}`}
        className={`group transition-all hover:border-primary/50 hover:shadow-md p-3 sm:p-4 ${readOnly ? '' : 'cursor-pointer'}`}
        onClick={() => onEdit?.(character)}
      >
        <div className="flex gap-3 sm:gap-4">
          {/* Portrait */}
          <div className="relative w-20 h-20 sm:w-24 sm:h-24 shrink-0 overflow-hidden rounded-xl border-2 border-primary/20">
            {hasPortrait ? (
              <Image
                src={mediaSrc(character.primaryPortraitUrl!)}
                alt={character.name}
                fill
                unoptimized
                /* The whole figure, framed from the top: a reference sheet is
                   a standing character on a plain background, so the head is
                   where the picture starts. */
                className="object-cover object-top"
                sizes="96px"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-primary/10 text-primary">
                <User className="h-8 w-8" />
              </div>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0 flex flex-col justify-center">
            <div className="flex items-start justify-between gap-2">
              <h3 className="font-medium text-sm sm:text-base line-clamp-1 group-hover:text-primary">
                {character.name}
              </h3>
              {!readOnly && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      data-testid="character-menu-button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 sm:h-8 sm:w-8 shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      data-testid="character-edit-button"
                      onClick={(e) => { e.stopPropagation(); onEdit?.(character); }}
                    >
                      <Pencil className="mr-2 h-4 w-4" />
                      {t('card.edit')}
                    </DropdownMenuItem>
                    {/* Shared, admin-managed defaults (owl/cat …) can't be deleted. */}
                    {!isSystemCharacter(character.id) && (
                      <DropdownMenuItem
                        data-testid="character-delete-button"
                        className="text-destructive focus:text-destructive"
                        onClick={(e) => { e.stopPropagation(); setShowDeleteDialog(true); }}
                      >
                        <Trash2 className="mr-2 h-4 w-4" />
                        {t('card.delete')}
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>


            {character.physicalDescription && (
              <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 mt-1">
                {character.physicalDescription}
              </p>
            )}
          </div>
        </div>
      </Card>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent data-testid="character-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('deleteConfirm.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('deleteConfirm.description', { name: character.name })}
              {deleteError && (
                <p className="mt-2 text-sm text-destructive">{deleteError}</p>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="character-delete-cancel-button" disabled={deleteCharacter.isPending}>
              {t('deleteConfirm.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="character-delete-confirm-button"
              onClick={(e) => {
                e.preventDefault();
                handleDelete();
              }}
              disabled={deleteCharacter.isPending}
              className="bg-destructive hover:bg-destructive/90"
            >
              {deleteCharacter.isPending ? t('deleteConfirm.confirming') : t('deleteConfirm.confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
